import { useEffect, useState } from "react";
import WebSocketService from "../../../services/WebSocketService";
import {
    getNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
} from "../../../api/notificationService";

export default function NotificationCenterPage() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadNotifications = async () => {
        setLoading(true);

        try {
            const data = await getNotifications();
            setNotifications(Array.isArray(data) ? data : []);
        } catch {
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNotifications();
    }, []);

    useEffect(() => {
        const ws = new WebSocketService();

        ws.onMessage = (raw) => {
            try {
                const msg = JSON.parse(raw);

                setNotifications((prev) => [
                    {
                        id: `${Date.now()}-${Math.random()}`,
                        title: msg.type || "Live Event",
                        message: msg.message || msg.trackingNumber || "New system event received",
                        createdAt: new Date().toISOString(),
                        isRead: false,
                        severity: msg.severity || "info",
                    },
                    ...prev,
                ]);
            } catch (e) {
                console.error("Notification WS error", e);
            }
        };

        ws.connect();

        return () => {
            if (typeof ws.close === "function") ws.close();
            else if (typeof ws.disconnect === "function") ws.disconnect();
        };
    }, []);

    const handleRead = async (id) => {
        setNotifications((prev) =>
            prev.map((n) => n.id === id ? { ...n, isRead: true } : n)
        );

        try {
            await markNotificationAsRead(id);
        } catch { }
    };

    const handleReadAll = async () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));

        try {
            await markAllNotificationsAsRead();
        } catch { }
    };

    return (
        <div style={{ padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                    <h2>Notification Center</h2>
                    <p style={{ color: "#666" }}>
                        Real-time logistics, IoT, blockchain, AI and security alerts.
                    </p>
                </div>

                <button onClick={handleReadAll} style={buttonStyle}>
                    Mark All as Read
                </button>
            </div>

            {loading ? (
                <p>Loading notifications...</p>
            ) : notifications.length === 0 ? (
                <p>No notifications yet.</p>
            ) : (
                <div style={{ display: "grid", gap: 12, marginTop: 20 }}>
                    {notifications.map((n) => (
                        <div
                            key={n.id}
                            style={{
                                ...cardStyle,
                                borderLeft: `8px solid ${getSeverityColor(n.severity)}`,
                                opacity: n.isRead ? 0.65 : 1,
                            }}
                        >
                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <b>{n.title || "Notification"}</b>
                                <span style={{ fontSize: 12, color: "#777" }}>
                                    {n.createdAt
                                        ? new Date(n.createdAt).toLocaleString()
                                        : "�"}
                                </span>
                            </div>

                            <p style={{ margin: "8px 0" }}>
                                {n.message || "No message"}
                            </p>

                            <div style={{ display: "flex", justifyContent: "space-between" }}>
                                <span style={{ fontSize: 13 }}>
                                    Status: {n.isRead ? "Read" : "Unread"}
                                </span>

                                {!n.isRead && (
                                    <button
                                        onClick={() => handleRead(n.id)}
                                        style={smallButtonStyle}
                                    >
                                        Mark as Read
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function getSeverityColor(severity) {
    switch ((severity || "").toLowerCase()) {
        case "critical":
            return "#C62828";
        case "warning":
            return "#EF6C00";
        case "success":
            return "#2E7D32";
        default:
            return "#1976D2";
    }
}

const cardStyle = {
    background: "white",
    border: "1px solid #ddd",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 3px 10px rgba(0,0,0,.08)",
};

const buttonStyle = {
    padding: "10px 14px",
    border: "none",
    borderRadius: 6,
    background: "#1976D2",
    color: "white",
    cursor: "pointer",
};

const smallButtonStyle = {
    ...buttonStyle,
    padding: "6px 10px",
    fontSize: 13,
};